import { Dropdown } from "primereact/dropdown";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";

import courseServices from "../../../services/courseServices";
import quizServices from "../../../services/quizServices";
import teacherServices from "../../../services/teacherServices";
import { getLanguage } from "../../../utils/getLanguage";
import Button from "../../Button";
import Modal from "../../Modal";

const SuggestedAssessmentsModal = forwardRef(({}, ref) => {
  const { t } = useTranslation();
  const lang = getLanguage();

  const [isOpen, setIsOpen] = useState(false);
  const [myQuestions, setMyQuestions] = useState([]);
  const [teacherCourses, setTeacherCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null); // Added state for selected course
  const [lessons, setLessons] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [activeTab, setActiveTab] = useState("suggested"); // tabs: suggested, my, ai

  useImperativeHandle(ref, () => ({
    openModal() {
      setIsOpen(true);
    },
    closeModal() {
      setIsOpen(false);
    },
  }));

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const getTeacherCourses = async () => {
    setLoading(true);
    try {
      const response = await teacherServices.getTeacherCourses();
      const formattedCourses = response?.map((course) => ({
        value: course?.id,
        label: course?.course,
      }));
      setTeacherCourses(formattedCourses);
    } catch (error) {
      console.error("Error fetching teacher stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCourse = async (selectedOption) => {
    if (selectedOption) {
      setSelectedCourse(selectedOption);
      setLoading(true);
      try {
        let response;

        switch (activeTab) {
          case "suggested":
            response = await quizServices.getSuggestedQuestionsBank(selectedOption);
            break;
          case "my":
            response = await quizServices.getTeacherQuestionsBank(selectedOption);
            break;
          default:
            response = { data: [] }; // Fallback response
        }

        setMyQuestions(response.data);
        setLessons(null);
        // getLessons(selectedOption);
      } catch (error) {
        console.error("Error getting course details", error);
      } finally {
        setLoading(false);
      }
    } else {
      setLessons([]); // Clear lessons if no course is selected
      setSelectedCourse(null);
    }
  };

  const handleSelectLesson = async (selectedOption) => {
    if (selectedOption) {
      setSelectedLesson(selectedOption);
      setLoading(true);
      try {
      } catch (error) {
        console.error("Error getting course details", error);
      } finally {
        setLoading(false);
      }
    } else {
      setLessons([]); // Clear lessons if no course is selected
      setSelectedCourse(null);
    }
  };

  const getLessons = async (courseID) => {
    setLoading(true);
    try {
      const response = await courseServices.getCourseLessons(courseID);
      const formattedlessons = response?.map((lesson) => ({
        value: lesson?.lesson?.id,
        label: lesson?.lesson?.title,
      }));
      setLessons(formattedlessons);
    } catch (error) {
      console.error("Error getting course lessons", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTeacherCourses();
  }, [lang]);

  useEffect(() => {
    handleSelectCourse(selectedCourse);
  }, [activeTab]);

  return (
    <Modal
      isFullScreen
      isOpen={isOpen}
      onClose={handleCloseModal}
      className="w-full max-w-2xl px-0"
      header={<p className="text-2xl text-blue-500">{t("Suggested Quizzes & Homeworks")}</p>}
    >
      <div className="mb-4 flex justify-center gap-2">
        {[
          { key: "my", label: t("My Questions") },
          { key: "suggested", label: t("Suggested Questions") },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
              activeTab === tab.key ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <Dropdown
        pt={{
          wrapper: "text-sm",
          header: "bg-blue-200 p-2 rounded-t-md",
          input:
            "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
          root: "w-full mb-2 rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
          panel: "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
          item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
        }}
        value={selectedCourse}
        onChange={(e) => handleSelectCourse(e.value)}
        options={teacherCourses}
        optionLabel="label"
        optionValue="value"
        emptyFilterMessage={t("no_results_found")}
        placeholder={t("select_course")}
        className="w-full"
        filter
      />

      {/* Lessons Dropdown */}
      {/* {selectedCourse && (
        <Dropdown
          pt={{
            wrapper: "text-sm",
            header: "bg-blue-200 p-2 rounded-t-md",
            input:
              "p-0 text-sm placeholder:text-sm placeholder:text-mutedText dark:text-dark-mutedText dark:text-white",
            root: "w-full rounded-md border bg-[#F9FAFB] px-3 py-2 focus:border-green-500 focus:ring focus:ring-green-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background",
            panel:
              "bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700",
            item: "p-2 hover:bg-green-100 dark:hover:bg-green-700",
          }}
          value={selectedLesson}
          options={lessons}
              emptyFilterMessage={t("no_results_found")}

          onChange={(e) => handleSelectLesson(e.value)}
          placeholder={t("select_lesson")}
          className="w-full"
          filter
        />
      )} */}

      <div className="text-center text-gray-700">
        {activeTab === "suggested" && (
          <div>
            {myQuestions.map((question, qIndex) => (
              <div
                key={qIndex}
                className="mb-4 rounded-lg border p-4 shadow-sm dark:border-gray-700 dark:bg-dark-background"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white">{question.text}</h4>
                    <div className="mt-2 flex justify-end space-x-2">
                      <Button
                        size="sm"
                        variant="outlined"
                        color="red"
                        label={t("delete")}
                        // onClick={() => handleDeleteQuestion(qIndex)}
                      />
                      <Button
                        size="sm"
                        variant="outlined"
                        label={t("edit")}
                        // onClick={() => handleEditQuestion(qIndex)}
                      />
                    </div>
                  </div>
                  <ul className="mt-2 space-y-2">
                    {question.options.map((option, oIndex) => (
                      <li key={oIndex} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          checked={option?.is_correct}
                          className="accent-green-200"
                          readOnly
                        />
                        <span
                          className={
                            option?.is_correct
                              ? "font-bold text-green-600 dark:text-green-400"
                              : "text-gray-700 dark:text-gray-300"
                          }
                        >
                          {option?.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === "my" && (
          <div>
            {myQuestions.map((question, qIndex) => (
              <div
                key={qIndex}
                className="mb-4 rounded-lg border p-4 shadow-sm dark:border-gray-700 dark:bg-dark-background"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white">{question.text}</h4>
                    <div className="mt-2 flex justify-end space-x-2">
                      <Button
                        size="sm"
                        variant="outlined"
                        color="red"
                        label={t("delete")}
                        // onClick={() => handleDeleteQuestion(qIndex)}
                      />
                      <Button
                        size="sm"
                        variant="outlined"
                        label={t("edit")}
                        // onClick={() => handleEditQuestion(qIndex)}
                      />
                    </div>
                  </div>
                  <ul className="mt-2 space-y-2">
                    {question.options.map((option, oIndex) => (
                      <li key={oIndex} className="flex items-center space-x-2">
                        <input
                          type="radio"
                          checked={option?.is_correct}
                          className="accent-green-200"
                          readOnly
                        />
                        <span
                          className={
                            option?.is_correct
                              ? "font-bold text-green-600 dark:text-green-400"
                              : "text-gray-700 dark:text-gray-300"
                          }
                        >
                          {option?.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
});

export default SuggestedAssessmentsModal;
